import { CircleSlash, ScrollText, TriangleAlert, Users } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api';
import type { AuditLogEntry, AuditOutcome, AuditQuery, RecordedReason } from '../../api/types';
import { Badge, type Tone } from '../../components/Badge';
import { Callout, FilterBar, StatCard, StatGrid, type Column } from '../../components/blocks';
import { CorrelationLink } from '../../components/Ids';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText } from '../../components/States';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { formatDateTime, formatRelative } from '../../lib/format';
import { useLabels } from '../../lib/labels';
import { useApi } from '../../lib/useApi';
import { FilterSelect, ListSection, PersonRef, RecordRef, RefreshButton, ResetButton, TextFilter, useAdminCrumbs, useParamSetter } from './ui';
import { SystemReason } from '../../components/SystemReason';

const OUTCOMES: AuditOutcome[] = ['SUCCESS', 'DENIED', 'FAILED'];
const ACTOR_TYPES = ['OPERATOR', 'CUSTOMER', 'SYSTEM', 'EXTERNAL'] as const;
const RESOURCE_TYPES = ['domain', 'user', 'customer', 'subject', 'dossier', 'abuse_case', 'domain_dispute', 'disclosure_case', 'registry_transaction', 'ledger_account'] as const;
const FILTER_KEYS = ['action', 'outcome', 'actor_type', 'actor_id', 'resource_type', 'resource_id', 'correlation_id'];

export const OUTCOME_TONE: Record<AuditOutcome, Tone> = { SUCCESS: 'ok', DENIED: 'serious', FAILED: 'crit' };

/** A recorded reason: the platform's own (lib/systemReason.ts) in the operator's language, original in the tooltip; an operator's text verbatim. */
export function AuditReason({ reason }: { reason: RecordedReason | null }) {
  const { t } = useTranslation('admin');
  if (typeof reason !== 'string' || !reason) return <span className="text-muted-foreground">{t('audit.noReason')}</span>;
  return <SystemReason reason={reason} />;
}

/** The action in words; the code stays one hover away (and in the drawer's technical details). */
export function ActionLabel({ action }: { action: string }) {
  const L = useLabels();
  return <span title={action}>{L.auditAction(action)}</span>;
}

/** `/admin/audit`: who did what, when and why. */
export function AuditPage() {
  const { t } = useTranslation('admin');
  useDocumentTitle(t('audit.title'));
  const crumbs = useAdminCrumbs(t('audit.title'));
  const [params, set, reset] = useParamSetter();
  const [open, setOpen] = useState<AuditLogEntry | null>(null);
  const q: AuditQuery = {
    action: params.get('action') || undefined,
    outcome: (params.get('outcome') || undefined) as AuditOutcome | undefined,
    actor_type: params.get('actor_type') || undefined,
    actor_id: params.get('actor_id') || undefined,
    resource_type: params.get('resource_type') || undefined,
    resource_id: params.get('resource_id') || undefined,
    correlation_id: params.get('correlation_id') || undefined,
    before: params.get('before') || undefined,
    limit: 50,
  };
  const result = useApi((signal) => api.listAudit(q, { signal }), [params.toString()]);
  const rows = result.status === 'success' ? result.data.items : [];
  const filtered = FILTER_KEYS.some((k) => params.get(k));

  const columns: Column<AuditLogEntry>[] = [
    {
      key: 'time',
      header: t('audit.col.time'),
      // The exact UTC timestamp is one hover (and the drawer) away; the column stays narrow.
      cell: (a) => (
        <span title={formatDateTime(a.created_at)} className="whitespace-nowrap">
          {formatRelative(a.created_at)}
        </span>
      ),
    },
    {
      key: 'actor',
      header: t('audit.col.actor'),
      cell: (a) => <PersonRef id={a.actor_id} name={a.actor_display_name} email={a.actor_email} automatedLabel={t(`actorType.${a.actor_type as 'SYSTEM'}`, { defaultValue: t('audit.automated') })} />,
    },
    { key: 'action', header: t('audit.col.action'), cell: (a) => <ActionLabel action={a.action} /> },
    { key: 'resource', header: t('audit.col.resource'), cell: (a) => <RecordRef resourceType={a.resource_type} resourceId={a.resource_id} resourceName={a.resource_name} /> },
    { key: 'outcome', header: t('audit.col.outcome'), cell: (a) => <Badge tone={OUTCOME_TONE[a.outcome]}>{t(`auditOutcome.${a.outcome}`)}</Badge> },
    {
      key: 'reason',
      header: t('audit.col.reason'),
      // Table cells default to `whitespace-nowrap`; a free-text reason has to wrap. The floor
      // goes on the content, not the cell: the table is `w-full`, so a cell width is only a
      // suggestion the browser drops on a narrow screen, and the reason ends up one word per
      // line. A minimum on the content makes the table wider than the card and scroll instead.
      className: 'whitespace-normal',
      cell: (a) => (
        <span className="block min-w-64 text-sm">
          <AuditReason reason={a.reason} />
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('audit.title')} crumbs={crumbs} subtitle={t('audit.subtitle')} actions={<RefreshButton onClick={result.reload} />} />
      <div className="space-y-4">
        <Callout tone="info">{t('note.immutable')}</Callout>
        {result.status === 'success' ? (
          <StatGrid>
            <StatCard label={t('audit.stat.entries')} value={rows.length} icon={ScrollText} />
            <StatCard
              label={t('audit.stat.refused')}
              value={rows.filter((a) => a.outcome === 'DENIED').length}
              hint={t('audit.stat.refusedHint')}
              icon={CircleSlash}
              tone={rows.some((a) => a.outcome === 'DENIED') ? 'serious' : 'ok'}
              status={rows.some((a) => a.outcome === 'DENIED') ? t('auditOutcome.DENIED') : undefined}
            />
            <StatCard
              label={t('audit.stat.failed')}
              value={rows.filter((a) => a.outcome === 'FAILED').length}
              hint={t('audit.stat.failedHint')}
              icon={TriangleAlert}
              tone={rows.some((a) => a.outcome === 'FAILED') ? 'crit' : 'ok'}
              status={rows.some((a) => a.outcome === 'FAILED') ? t('auditOutcome.FAILED') : undefined}
            />
            <StatCard label={t('audit.stat.people')} value={new Set(rows.map((a) => a.actor_id)).size} icon={Users} />
          </StatGrid>
        ) : null}
        <FilterBar actions={<ResetButton onClick={reset} disabled={!filtered} />}>
          <TextFilter label={t('audit.filterAction')} value={q.action ?? ''} placeholder={t('audit.filterActionPlaceholder')} onApply={(v) => set('action', v)} />
          <FilterSelect
            label={t('audit.filterOutcome')}
            value={q.outcome ?? ''}
            onChange={(v) => set('outcome', v)}
            options={OUTCOMES.map((o) => ({ value: o, label: t(`auditOutcome.${o}`) }))}
          />
          <FilterSelect
            label={t('audit.filterActor')}
            value={q.actor_type ?? ''}
            onChange={(v) => set('actor_type', v)}
            options={ACTOR_TYPES.map((a) => ({ value: a, label: t(`actorType.${a}`) }))}
          />
          <FilterSelect
            label={t('audit.filterResourceType')}
            value={q.resource_type ?? ''}
            onChange={(v) => set('resource_type', v)}
            className="min-w-40"
            options={RESOURCE_TYPES.map((r) => ({ value: r, label: t(`resourceType.${r}`) }))}
          />
          <TextFilter label={t('audit.filterResourceId')} value={q.resource_id ?? ''} placeholder={t('audit.filterResourceIdPlaceholder')} onApply={(v) => set('resource_id', v)} />
          <TextFilter label={t('audit.filterCorrelation')} value={q.correlation_id ?? ''} placeholder={t('audit.filterCorrelationPlaceholder')} onApply={(v) => set('correlation_id', v)} />
        </FilterBar>
        <ListSection
          result={result}
          rows={rows}
          columns={columns}
          rowKey={(a) => a.id}
          onRowClick={setOpen}
          description={result.status === 'success' ? t('audit.count', { count: rows.length }) : undefined}
          errorTitle={t('audit.loadError')}
          empty={t('audit.empty')}
          nextBefore={result.status === 'success' ? result.data.next_before : null}
          onNext={(c) => set('before', c)}
          onFirst={() => set('before', '')}
          first={!q.before}
        />
      </div>
      <EntryDrawer entry={open} onClose={() => setOpen(null)} />
    </>
  );
}

/** The whole entry, including the values recorded before and after the change. */
export function EntryDrawer({ entry: a, onClose }: { entry: AuditLogEntry | null; onClose: () => void }) {
  const { t } = useTranslation('admin');
  if (!a) return null;
  const hasValues = a.before !== null || a.after !== null;
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full gap-0 overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{t('audit.drawer.title')}</SheetTitle>
          <SheetDescription>
            <ActionLabel action={a.action} />
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4 px-4 pb-6">
          <KeyValue
            rows={[
              [t('audit.drawer.when'), formatDateTime(a.created_at)],
              [
                t('audit.drawer.who'),
                <PersonRef key="w" id={a.actor_id} name={a.actor_display_name} email={a.actor_email} automatedLabel={t(`actorType.${a.actor_type as 'SYSTEM'}`, { defaultValue: t('audit.automated') })} />,
              ],
              [t('audit.drawer.accountKind'), t(`actorType.${a.actor_type as 'OPERATOR'}`, { defaultValue: a.actor_type })],
              [t('audit.drawer.action'), <ActionLabel key="a" action={a.action} />],
              [t('audit.drawer.recordKind'), t(`resourceType.${a.resource_type as 'domain'}`, { defaultValue: a.resource_type })],
              [t('audit.drawer.record'), <RecordRef key="r" resourceType={a.resource_type} resourceId={a.resource_id} resourceName={a.resource_name} />],
              [t('audit.drawer.outcome'), <Badge key="o" tone={OUTCOME_TONE[a.outcome]}>{t(`auditOutcome.${a.outcome}`)}</Badge>],
              [t('audit.drawer.reason'), <AuditReason key="n" reason={a.reason} />],
            ]}
          />
          {hasValues ? (
            <div className="space-y-3">
              {a.before !== null ? (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-muted-foreground">{t('audit.drawer.before')}</div>
                  <pre className="overflow-x-auto rounded-md border bg-muted/40 p-3 text-xs">{JSON.stringify(a.before, null, 2)}</pre>
                </div>
              ) : null}
              {a.after !== null ? (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-muted-foreground">{t('audit.drawer.after')}</div>
                  <pre className="overflow-x-auto rounded-md border bg-muted/40 p-3 text-xs">{JSON.stringify(a.after, null, 2)}</pre>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('audit.drawer.noChange')}</p>
          )}
          <KeyValue
            rows={[
              [t('audit.drawer.actionCode'), <code key="ac" className="text-xs">{a.action}</code>],
              [t('audit.drawer.correlation'), a.correlation_id ? <CorrelationLink key="c" id={a.correlation_id} /> : '—'],
              [t('audit.drawer.request'), a.request_id ? <CopyText key="q" value={a.request_id} /> : '—'],
              [t('audit.drawer.id'), <CopyText key="i" value={a.id} />],
            ]}
          />
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            {t('audit.drawer.close')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
